import { reactive, ref } from 'vue';
import {
    ClientResponse, ClientResponseOfModuleConfigurationModel, ModuleConfigurationImage,
    ProductInternalModuleConfiguratorClientService, SaveModuleConfigurationRequest,
} from '@/types/productServerApi';
import { uuid } from '@/core/autocomplete/utils/uuid.util';
import pageStore from '@/shared/content/page.store';
import { ModuleConfiguratorTileModel } from '@/types/B2CcontentServerContract';
import useTEMPB2CFeatureBasket from '@/b2c/basket/useTEMPB2CFeatureBasket';
import useBasket from '@/storecontext/basket/useBasket';
import { hardRedirect } from '@/core/navigation/redirect.service';
import dictionaryService from '@/core/dictionary/dictionary.service';
import useTEMPB2BFeatureBasket from '@/b2b/basket/useTEMPB2BFeatureBasket';

const is3DConfiguratorFeatureEnabled = ref<boolean>(false);
const isConfiguratorToggled = ref<boolean>(false);
const configurationData = ref<ConfiguratorModel | null>(null);
const initialLegs = ref<string>();
const moduleConfiguratorTileData = ref<any>(null);
const configurationImages = ref<ModuleConfigurationImage[]>([]);
const configurationLabels: {
    name?: string;
    moduleCountTitle: string;
    savingConfiguration: string;
    moduleCountTitlePlural;
} = reactive({
    moduleCountTitle: '{name} - {count} module',
    moduleCountTitlePlural: '{name} - {count} modules',
    savingConfiguration: 'Saving configuration...',
});

const { addOrUpdateModuleConfiguration: addOrUpdateModuleConfigurationB2C } = useTEMPB2CFeatureBasket();
const { addOrUpdateModuleConfiguration: addOrUpdateModuleConfigurationB2B } = useTEMPB2BFeatureBasket();
const { addOrUpdateModuleConfiguration: addOrUpdateModuleConfigurationStoreContext } = useBasket();
const textLabels = ref<any>(null);

// TEST DATA
const conf1 = '{"models":[{"model":"01-081-70","position":{"x":0,"y":0,"z":0.45500001311302185},"rotation":{"x":0,"y":0,"z":0},"customization":{},"uid":41}],"productSeries":"caisa","name":"Default","material1":{"group":"Nantes","name":"Light Grey"},"material2":{"group":null,"name":"brushed_steel"},"roomConfig":{"showBackWall":true,"showLeftWall":true,"showRightWall":true,"width":8,"depth":6,"height":2.4,"floorMaterial":"Herringbone_Medium_02_2mx2m","wallMaterial":"Wall_MoonlightElegance"}}';

export interface ConfiguratorModel{
    name: string;
    material1: any;
    models: ConfiguratorModelItem[];
    productSeries: string;
    roomConfig: any;
}

export interface ConfiguratorModelItem{
        model: string;
        position: {
            x: number;
            y: number;
            z: number;
        };
        rotation: {
            x: number;
            y: number;
            z: number;
        };
        customization: any;
        uid: number;
}

export function useModuleConfigurator() {
    const initialConfig = ref<string>('');

    const newEditGuid = uuid();

    const closeConfigurator = () => {
        isConfiguratorToggled.value = false;
        toggleHideStyleSheet(true);
    };

    const launchConfigurator = () => {
        isConfiguratorToggled.value = true;
    };

    const toggleConfigurator = () => {
        isConfiguratorToggled.value = true;
    };

    const toggleHideStyleSheet = (disabled: boolean) => {
        const linkElement = document.head.querySelector('[href*="slim-configurator.css"]') as HTMLLinkElement;

        if (linkElement) {
            linkElement.disabled = disabled;
        };
    };

    const setConfigurator = async(configurator: ModuleConfiguratorTileModel, configurationId?: string, configurationName?: string) => {
        is3DConfiguratorFeatureEnabled.value = configurator.isEnabled;
        textLabels.value = configurator.dictionaryLabels.length ? configurator.dictionaryLabels : fallbackLabels.texts;

        if (configurator.isEnabled && configurator.modules && configurationId) {
            await getConfigurationData(configurationId, configurator.modules, configurationName);
        }
    };

    const clearConfigurator = () => {
        is3DConfiguratorFeatureEnabled.value = false;
        moduleConfiguratorTileData.value = null;
    };

    const getConfigurationData = async(configurationId, modules, configurationName) => {
        const result = await ProductInternalModuleConfiguratorClientService.getModuleConfiguration(configurationId);

        configurationData.value = result.model?.configuration ?? {} as ConfiguratorModel;
        configurationImages.value = result.model?.images ?? [];
        const checkoutImage = result.model?.images?.[0]?.content || '';
        moduleConfiguratorTileData.value = {
            modules: modules,
            configuration: {
                name: configurationName,
                image: checkoutImage,
                ...result.model?.dimensions,
            },
        };
    };

    const setConfig = (config:string) => {
        if (!window.ModuleConfigurator) return;
        window.ModuleConfigurator.loadConfigurationFromJSON(conf1);
    };

    const launchPreviousConfigurator = (value:string) => {
        initialConfig.value = conf1;
        launchConfigurator();
    };

    const resetConfigurator = () => {
        // initialConfig.value = '';
        // configurationData.value = null;
        // configurationId.value = '';

        hardRedirect(window.location.pathname); // TODO - remove when spa reload is fixed, should also only remove edit and configurationId from url
    };

    const clearConfig = () => {
        window.ModuleConfigurator.dispose();
    };

    const getConfig = () => {
        const d = window.ModuleConfigurator.getConfigurationJSON();
        console.log(d);
        console.log(JSON.parse(d));
    };

    const getSummary = () => {
        const d = window.ModuleConfigurator.getConfigurationSummary();
        console.log(d);
    };

    const getImage = (angle: string) => {
        window.ModuleConfigurator.getConfigurationImage(angle, 1000).then((img) => {
            console.log(img);
        });
    };

    const getAllImages = () => {
        window.ModuleConfigurator.getAllImages().then((images) => {
            console.log(images);
        });
    };

    const setInitialLegs = (legs: string) => {
        initialLegs.value = legs;
    };

    const resetModuleConfiguration = () => {
        configurationData.value = null;
        configurationImages.value = [];
    };

    const setConfigurationLabels = ({ name, moduleCountTitle, moduleCountTitlePlural }) => {
        configurationLabels.name = name;
        configurationLabels.moduleCountTitle = moduleCountTitle;
        configurationLabels.moduleCountTitlePlural = moduleCountTitlePlural;
    };

    const getMeasure = () => {
        window.ModuleConfigurator.getConfigurationImage('front', 1024, 1024, true).then((img) => {
            const image = new Image();
            image.src = img;
            const newTab = window.open();
            if (newTab) {
                newTab.document.body.innerHTML = image.outerHTML;
            }
        });
    };

    const saveConfiguration = async(configurationId, editGuid) => {
        const config = JSON.parse(window.ModuleConfigurator.getConfigurationJSON());
        const summary = window.ModuleConfigurator.getConfigurationSummary();

        let name = dictionaryService.formatNamed(configurationLabels.moduleCountTitle, { name: config.productSeries, count: config.models.length });

        if (config.models.length > 1) {
            name = dictionaryService.formatNamed(configurationLabels.moduleCountTitlePlural, { name: config.productSeries, count: config.models.length });
        }

        const request:SaveModuleConfigurationRequest = {
            name: name,
            configuration: config,
            ...summary,
            images: [],
        };

        await window.ModuleConfigurator.getAllImages().then((images) => {
            request.images = images.map((img) => {
                return {
                    content: img,
                } as ModuleConfigurationImage;
            });
        });

        const guid = configurationId.value || uuid();
        const messageId = 'moduleConfiguration';

        const sendModuleConfiguration = async(request: SaveModuleConfigurationRequest, messageId?: string): Promise<ClientResponseOfModuleConfigurationModel | ClientResponse> => {
            return await ProductInternalModuleConfiguratorClientService.saveModuleConfiguration(guid, request, messageId)
                .then((response) => {
                    return response;
                });
        };

        if (editGuid.value) {
            const updateRequest = {
                configuration: {
                    ...request.dimensions,
                    image: request.images[0]?.content ?? '',
                    name: request.name,
                },
                modules: request.modules,
            };

            if (pageStore.isB2CPageLayout) {
                await addOrUpdateModuleConfigurationB2C(guid, updateRequest);
            } else if (pageStore.isB2BPageLayout) {
                await addOrUpdateModuleConfigurationB2B(guid, updateRequest);
            } else if (pageStore.isStoreContextPageLayout) {
                await addOrUpdateModuleConfigurationStoreContext(guid, updateRequest);
            }

            return sendModuleConfiguration(request, messageId).then((e) => {
                return { guid: guid, redirectId: request.modules[0].sku, editGuid: editGuid.value };
            });
        }

        return sendModuleConfiguration(request, messageId).then((e) => {
            return { guid: guid, redirectId: request.modules[0].sku, editGuid: null };
        });
    };

    return {
        isConfiguratorToggled,
        toggleConfigurator,
        launchConfigurator,
        closeConfigurator,
        newEditGuid,
        toggleHideStyleSheet,
        is3DConfiguratorFeatureEnabled,
        setConfigurator,
        clearConfigurator,
        setConfig,
        launchPreviousConfigurator,
        resetConfigurator,
        clearConfig,
        getConfig,
        getSummary,
        getImage,
        getAllImages,
        initialConfig,
        saveConfiguration,
        configurationData,
        initialLegs,
        setInitialLegs,
        setConfigurationLabels,
        textLabels,
        getMeasure,
        resetModuleConfiguration,
        fallbackLabels,
        moduleConfiguratorTileData,
        configurationLabels,
        configurationImages,
    };
}

// Fallback until there actual translations are available
export const fallbackLabels = {
    texts: [
        {
            key: 'ButtonAddItem',
            value: 'Add item',
        },
        {
            key: 'ButtonMaterials',
            value: 'Color and material',
        },
        {
            key: 'ButtonLegs',
            value: 'Legs',
        },
        {
            key: 'ButtonMyConfiguration',
            value: 'My design',
        },
        {
            key: 'ButtonRoom',
            value: 'Room',
        },
        {
            key: 'ButtonSettings',
            value: 'Settings',
        },
        {
            key: 'PanelTitleSettings',
            value: 'Settings',
        },
        {
            key: 'PanelTitleRoom',
            value: 'Customize Room',
        },
        {
            key: 'ToggleTitleSnapping',
            value: 'Snap items',
        },
        {
            key: 'ToggleInfoSnapping',
            value: 'Easily align items to each other',
        },
        {
            key: 'ToggleTitleGrid',
            value: 'Show grid',
        },
        {
            key: 'ToggleInfoGrid',
            value: 'Display grid on floor',
        },
        {
            key: 'ToggleTitleAutoframing',
            value: 'Camera auto-framing',
        },
        {
            key: 'ToggleInfoAutoframing',
            value: 'Automatically center in viewport',
        },
        {
            key: 'ToggleTitleLockforedit',
            value: 'Lock for edit – \'view only\' mode',
        },
        {
            key: 'ToggleInfoLockforedit',
            value: 'Prevent items from being unintentionally moved around',
        },
        {
            key: 'ToggleTitleMeasurements',
            value: 'Show measurements',
        },
        {
            key: 'ToggleInfoMeasurements',
            value: 'Display measurement of your combined configuration',
        },
        {
            key: 'PanelTitleChangeMaterial',
            value: 'Select Color and Material',
        },
        {
            key: 'PanelTitleAllVariants',
            value: 'All variants',
        },
        {
            key: 'ButtonExpandAllVariants',
            value: 'Explore all variants',
        },
        {
            key: 'PanelBackButton',
            value: 'Back',
        },
        {
            key: 'PanelTitleChangeLegs',
            value: 'Select Legs',
        },
        {
            key: 'PanelTitleAddItem',
            value: 'Add Item',
        },
        {
            key: 'LabelMaterialType',
            value: 'Material Type',
        },
        {
            key: 'LabelMaterialColor',
            value: 'Color',
        },
        {
            key: 'LabelMaterialColorAndMaterial',
            value: 'Color and material',
        },
        {
            key: 'LabelLegs',
            value: 'Legs',
        },
        {
            key: 'NoMatchingMaterials',
            value: 'No materials match the selected combination of color and type.',
        },
        {
            key: 'RoomSectionColors',
            value: 'Colors',
        },
        {
            key: 'RoomLabelWallColor',
            value: 'Wall Color',
        },
        {
            key: 'RoomLabelFloorColor',
            value: 'Floor',
        },
        {
            key: 'RoomSectionDimensions',
            value: 'Dimensions',
        },
        {
            key: 'RoomLabelDimensionsWidth',
            value: 'Room Width (M)',
        },
        {
            key: 'RoomInfoDimensionsWidth',
            value: 'Useable room width in meters',
        },
        {
            key: 'RoomLabelDimensionsDepth',
            value: 'Room Depth (M)',
        },
        {
            key: 'RoomInfoDimensionsDepth',
            value: 'Useable room depth in meters',
        },
        {
            key: 'RoomLabelDimensionsHeight',
            value: 'Room Height (M)',
        },
        {
            key: 'RoomInfoDimensionsHeight',
            value: 'Ceiling height in meters',
        },
        {
            key: 'RoomSectionWalls',
            value: 'Wall Display',
        },
        {
            key: 'RoomLabelShowBackwall',
            value: 'Back Wall',
        },
        {
            key: 'RoomInfoShowBackwall',
            value: 'Show back wall',
        },
        {
            key: 'RoomLabelShowLeftwall',
            value: 'Left Wall',
        },
        {
            key: 'RoomInfoShowLeftwall',
            value: 'Show left wall',
        },
        {
            key: 'RoomLabelShowRightwall',
            value: 'Right Wall',
        },
        {
            key: 'RoomInfoShowRightwall',
            value: 'Show right wall',
        },
        {
            key: 'ButtonApplyRoomChanges',
            value: 'Apply Changes',
        },
        {
            key: 'PanelTitleMyconfig',
            value: 'My design',
        },
        {
            key: 'ButtonResetConfiguration',
            value: 'Reset',
        },
        {
            key: 'ButtonProceedCheckout',
            value: 'Save design',
        },
        {
            key: 'GenericOk',
            value: 'Ok',
        },
        {
            key: 'GenericCancel',
            value: 'Cancel',
        },
        {
            key: 'GenericConfirm',
            value: 'Confirm',
        },
        {
            key: 'GenericYes',
            value: 'Yes',
        },
        {
            key: 'GenericNo',
            value: 'No',
        },
        {
            key: 'WidgetTitleLocked',
            value: 'Locked for editing',
        },
        {
            key: 'LabelConfigLocked',
            value: 'The configuration is locked for editing.',
        },
        {
            key: 'ButtonUnlock',
            value: 'Click here to unlock',
        },
        {
            key: 'PanelTitleIssuesSingle',
            value: 'Problem detected',
        },
        {
            key: 'PanelTitleIssuesPlural',
            value: '{0} problems detected',
        },
        {
            key: 'IssueSeatingDepthMismatchTitle',
            value: 'Mixed seating depths',
        },
        {
            key: 'IssueSeatingDepthMismatchBody',
            value: 'You have combined modules with different seating depths. This might not be your intention.',
        },
        {
            key: 'IssueSeatingDepthMismatchActionRemove',
            value: 'Remove and choose another',
        },
        {
            key: 'IssueSeatingDepthMismatchActionKeep',
            value: 'Keep selected',
        },
        {
            key: 'MaterialTypesAll',
            value: 'All types',
        },
        {
            key: 'ResetDialogTitle',
            value: 'Reset Configuration',
        },
        {
            key: 'ResetDialogBody',
            value: 'Really delete the current configuration and start over?',
        },
        {
            key: 'ResetDialogButtonConfirm',
            value: 'Yes, reset',
        },
        {
            key: 'InfoEmptyConfiguration',
            value: 'No items in configuration.',
        },
        {
            key: 'InfoEmptyConfigurationHelp',
            value: 'Click here to add your first item!',
        },
        {
            key: 'LockedDialogTitle',
            value: 'Configuration is locked',
        },
        {
            key: 'LockedDialogBody',
            value: 'To edit the current configuration please first unlock it.',
        },
        {
            key: 'BasketLabelPriceInclVat',
            value: 'Price total (Incl. 25% VAT)',
        },
        {
            key: 'BasketLabelUpholstery',
            value: 'Upholstery:',
        },
        {
            key: 'BasketLabelLegs',
            value: 'Leg material:',
        },
        {
            key: 'WidgetMeasurementsTitle',
            value: 'Total size of design',
        },
        {
            key: 'WidgetMeasurementsWidth',
            value: 'Width',
        },
        {
            key: 'WidgetMeasurementsHeight',
            value: 'Height',
        },
        {
            key: 'WidgetMeasurementsDepth',
            value: 'Depth',
        },
        {
            key: 'WidgetMeasurementsSeatdepth',
            value: 'Seating Depth',
        },
        {
            key: 'WidgetMeasurementsSeatheight',
            value: 'Seating Height',
        },
        {
            key: 'UserguideAdditemTitle',
            value: 'Add new item',
        },
        {
            key: 'UserguideAdditemBody',
            value: 'Go here to add new items and modules to your configuration.',
        },
        {
            key: 'UserguideMaterialsTitle',
            value: 'Change materials',
        },
        {
            key: 'UserguideMaterialsBody',
            value: 'Make a personal expression and choose among our wide assortment of materials and upholsteries.',
        },
        {
            key: 'UserguideFunctionsTitle',
            value: 'Function buttons',
        },
        {
            key: 'UserguideFunctionsBody',
            value: 'The dynamic button bar lets you manipulate currently selected modules and provides quick access to other useful operations. Give it a try!',
        },
        {
            key: 'UserguideRoomTitle',
            value: 'Make yourself at home',
        },
        {
            key: 'UserguideRoomBody',
            value: 'Customize the size and appearance of the room to better visualize the furniture in your own space.',
        },
        {
            key: 'UserguideSettingsTitle',
            value: 'Adjust your workspace',
        },
        {
            key: 'UserguideSettingsBody',
            value: 'Fine-tune the way our configurator behaves to fit how you prefer it to work.',
        },
        {
            key: 'UserguideSummaryTitle',
            value: 'Summary of your choices',
        },
        {
            key: 'UserguideSummaryBody',
            value: 'Get a quick overview of your current configuration and bring it to your basket.',
        },
        {
            key: 'FloatbuttonHintIssues',
            value: 'Issues',
        },
        {
            key: 'FloatbuttonHintCamera',
            value: 'Camera angle',
        },
        {
            key: 'FloatbuttonHintGuide',
            value: 'Quick Guide',
        },
        {
            key: 'FloatbuttonHintSelectAll',
            value: 'Select All',
        },
        {
            key: 'FloatbuttonHintRotateCw',
            value: 'Rotate clockwise',
        },
        {
            key: 'FloatbuttonHintRotateCww',
            value: 'Rotate counter-clockwise',
        },
        {
            key: 'FloatbuttonHintDuplicate',
            value: 'Duplicate selected item(s)',
        },
        {
            key: 'FloatbuttonHintDelete',
            value: 'Delete selected item(s)',
        },
        {
            key: 'LabelDiscountCampaign',
            value: 'Spar {{PCT}}',
        },
        {
            key: 'LabelDiscountMyboliaboost',
            value: 'Medlemsrabat {{PCT}}',
        },
        {
            key: 'LabelSavingPercentage',
            value: 'Spar {{PCT}}',
        },
        {
            key: 'BasketLabelPriceExclVat',
            value: 'Net price total',
        },
    ],
};
